/**
 * CounselCore™ Unified Document Creator
 * Phase 2: Workflow-Centric Document Creation
 * 
 * A guided multi-step wizard for creating all clinical documents from a single entry point.
 * Ensures PerformCare compliance and proper document filing.
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  FileText, ClipboardList, Mail, Sparkles, Mic, PenLine,
  ChevronRight, ChevronLeft, Check, Loader2, AlertCircle,
  User, Calendar, Target, Save, X, Wand2, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document type configurations
const DOCUMENT_TYPES = {
  progress_note: {
    id: 'progress_note',
    name: 'Progress Note',
    description: 'Session documentation (SOAP format recommended)',
    icon: FileText,
    color: 'blue',
    saveTo: 'Progress Notes',
    requiresSession: false,
    aiSupported: true,
    voiceSupported: true
  },
  treatment_plan: {
    id: 'treatment_plan',
    name: 'Treatment Plan',
    description: 'PerformCare-compliant treatment plan',
    icon: Target,
    color: 'green',
    saveTo: 'Treatment Plans',
    requiresSession: false,
    aiSupported: true,
    voiceSupported: false
  },
  parent_email: {
    id: 'parent_email',
    name: 'Parent Communication',
    description: 'Session summary email for parents/guardians',
    icon: Mail,
    color: 'purple',
    saveTo: 'Email Drafts',
    requiresSession: false,
    aiSupported: true,
    voiceSupported: false,
    requiresGuardian: true
  }
};

const CREATION_METHODS = {
  manual: {
    id: 'manual',
    name: 'Write Manually',
    description: 'Type or paste your content directly',
    icon: PenLine,
    color: 'gray'
  },
  ai_assisted: {
    id: 'ai_assisted',
    name: 'Smart-Assisted',
    description: 'Generate from session notes or bullet points',
    icon: Sparkles,
    color: 'indigo'
  },
  voice: {
    id: 'voice',
    name: 'Voice Transcription',
    description: 'Transcribe audio recording (requires Whisper API key)',
    icon: Mic,
    color: 'rose'
  }
};

const NOTE_TYPES = [
  { value: 'progress', label: 'Progress Note (Every Session)' },
  { value: 'treatment_plan', label: 'Treatment Plan (Quarterly)' },
  { value: 'ba_plan', label: 'BA Plan (Quarterly if BA on case)' },
  { value: 'monthly_summary', label: 'Monthly Summary (Due 5th)' }
];

// PerformCare-specific constants
const PERFORMCARE_PLAN_TYPES = [
  { value: 'cmo_tisp', label: 'CMO - TISP (Treatment ISP)' },
  { value: 'cmo_90day', label: 'CMO - 90-Day Review' },
  { value: 'cmo_sc', label: 'CMO - Service Change' },
  { value: 'iic_plan', label: 'IIC Treatment Plan' },
  { value: 'bps_asmt', label: 'BPS Assessment' },
  { value: 'strengths_needs', label: 'Strengths & Needs (Routine)' }
];

const PERFORMCARE_SUBTYPES = [
  { value: 'behavioral_health', label: 'Behavioral Health' },
  { value: 'trauma', label: 'Trauma-Focused' },
  { value: 'family_therapy', label: 'Family Therapy' },
  { value: 'crisis_stabilization', label: 'Crisis Stabilization' },
  { value: 'ba_behavioral', label: 'BA - Behavioral Assistance' }
];

const NEED_DOMAINS = [
  'Mental Health', 'Education', 'Family', 'Social', 
  'Behavioral', 'Medical', 'Substance Use', 'Legal'
];

const UnifiedDocumentCreator = ({ 
  open, 
  onClose, 
  token, 
  clients = [], 
  sessions = [],
  onDocumentCreated,
  toast,
  initialClientId = null,
  initialDocType = null
}) => {
  // Wizard state
  const [step, setStep] = useState(1);
  const [docType, setDocType] = useState(initialDocType || null);
  const [creationMethod, setCreationMethod] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(initialClientId || '');
  
  // Form data
  const [formData, setFormData] = useState({
    content: '',
    noteType: 'progress',
    sessionId: '',
    serviceDate: new Date().toISOString().split('T')[0],
    serviceDateReason: '',
    diagnosisCode: '',
    subject: '',
    // AI-specific fields
    rawNotes: '',
    sessionType: 'Individual Therapy',
    currentGoals: [],
    // PerformCare-specific fields for Treatment Plans
    pcPlanType: '',
    pcPlanSubtype: '',
    pcYouthVision: '',
    pcFamilyVision: '',
    pcStrengths: [{ description: '', personLinked: '' }],
    pcNeeds: [{ description: '', domain: 'Mental Health', progress: 'Not Met' }],
    pcStrategies: [{ description: '', responsiblePerson: '' }],
    pcDiagnosis: { code: '', description: '' }
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [error, setError] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Get selected client details
  const selectedClient = clients.find(c => c.id === selectedClientId);
  const hasGuardian = selectedClient?.guardian_name || selectedClient?.guardian_email;
  const availableSessions = sessions.filter(s => s.client_id === selectedClientId);
  
  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setDocType(initialDocType || null);
      setCreationMethod(null);
      setSelectedClientId(initialClientId || '');
      setFormData({
        content: '',
        noteType: 'progress',
        sessionId: '',
        serviceDate: new Date().toISOString().split('T')[0],
        serviceDateReason: '',
        diagnosisCode: '',
        subject: '',
        rawNotes: '',
        sessionType: 'Individual Therapy',
        currentGoals: [],
        // PerformCare fields reset
        pcPlanType: '',
        pcPlanSubtype: '',
        pcYouthVision: '',
        pcFamilyVision: '',
        pcStrengths: [{ description: '', personLinked: '' }],
        pcNeeds: [{ description: '', domain: 'Mental Health', progress: 'Not Met' }],
        pcStrategies: [{ description: '', responsiblePerson: '' }],
        pcDiagnosis: { code: '', description: '' }
      });
      setError('');
      setGeneratedContent('');
      setSaveSuccess(false);
    }
  }, [open, initialClientId, initialDocType]);
  
  // Auto-advance if initial values provided
  useEffect(() => {
    if (open && initialDocType && step === 1) {
      setDocType(initialDocType);
    }
    if (open && initialClientId && step === 2) {
      setSelectedClientId(initialClientId);
    }
  }, [open, initialDocType, initialClientId, step]);
  
  // Calculate total steps based on method
  const getTotalSteps = () => {
    if (creationMethod === 'ai_assisted') return 5;
    return 4;
  };
  
  // Step validation
  const canProceed = () => {
    switch (step) {
      case 1:
        return docType !== null;
      case 2:
        return selectedClientId !== '';
      case 3:
        return creationMethod !== null;
      case 4:
        if (creationMethod === 'ai_assisted') {
          return formData.rawNotes.trim().length > 0;
        }
        // For manual treatment plans, require at least plan type and one strength/need
        if (docType === 'treatment_plan') {
          return formData.pcPlanType && 
                 formData.pcStrengths.some(s => s.description.trim()) &&
                 formData.pcNeeds.some(n => n.description.trim());
        }
        return formData.content.trim().length > 0;
      case 5:
        return formData.content.trim().length > 0 || generatedContent.trim().length > 0;
      default:
        return true;
    }
  };
  
  // Handle AI generation
  const handleAIGenerate = async () => {
    if (!formData.rawNotes.trim()) {
      toast?.error('Please enter session notes or bullet points first');
      return;
    }
    
    setAiGenerating(true);
    setError('');
    
    try {
      let endpoint = '';
      let payload = {};
      
      const clientName = selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Client';
      const parentName = selectedClient?.guardian_name || '';
      
      switch (docType) {
        case 'progress_note':
          endpoint = '/api/ai/generate-soap-note';
          payload = {
            raw_notes: formData.rawNotes,
            client_name: clientName,
            session_type: formData.sessionType,
            session_date: formData.serviceDate,
            format_type: 'soap'
          };
          break;
        case 'treatment_plan':
          endpoint = '/api/ai/generate-treatment-update';
          payload = {
            session_notes: formData.rawNotes,
            client_name: clientName,
            current_goals: formData.currentGoals,
            diagnosis: selectedClient?.diagnosis || '',
            sessions_completed: availableSessions.filter(s => s.status === 'completed').length
          };
          break;
        case 'parent_email':
          endpoint = '/api/ai/generate-parent-summary';
          payload = {
            session_notes: formData.rawNotes,
            client_name: selectedClient?.first_name || 'Client',
            parent_name: parentName,
            session_date: formData.serviceDate,
            session_type: formData.sessionType,
            include_homework: true,
            tone: 'warm'
          };
          break;
        default:
          throw new Error('Unknown document type');
      }
      
      const response = await axios.post(`${API_URL}${endpoint}`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        // Extract generated content based on response format
        let content = '';
        if (docType === 'progress_note') {
          content = response.data.formatted_note || response.data.soap_note || '';
        } else if (docType === 'treatment_plan') {
          content = response.data.treatment_update || '';
        } else if (docType === 'parent_email') {
          content = response.data.email_content || response.data.parent_summary || '';
        }
        
        setGeneratedContent(content);
        setFormData(prev => ({ ...prev, content: content }));
        
        // AUTO-SAVE: Save as draft immediately after generation
        try {
          await autoSaveAsDraft(content);
          toast?.success('Document generated and auto-saved as draft! Review and finalize.');
        } catch (autoSaveErr) {
          console.warn('Auto-save failed, but content is available for manual save:', autoSaveErr);
          toast?.success('Document generated successfully! Review and save when ready.');
        }
        
        setStep(5); // Move to review step
      } else {
        throw new Error(response.data.error || 'Failed to generate document');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setError(err.message || 'Failed to generate document. Please try again.');
      toast?.error('AI generation failed. Please try again.');
    } finally {
      setAiGenerating(false);
    }
  };
  
  // Auto-save as draft after AI generation
  const autoSaveAsDraft = async (content) => {
    const clientName = selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Client';
    
    let endpoint = '';
    let payload = {};
    
    switch (docType) {
      case 'progress_note':
        endpoint = '/api/ai/save-soap-note';
        payload = {
          client_id: selectedClientId,
          client_name: clientName,
          content: content,
          session_date: formData.serviceDate,
          session_type: formData.sessionType,
          auto_saved: true,
          status: 'ai_draft'
        };
        break;
      case 'treatment_plan':
        endpoint = '/api/ai/save-treatment-plan';
        payload = {
          client_id: selectedClientId,
          client_name: clientName,
          content: content,
          plan_type: formData.pcPlanType || 'Auto-Generated Draft',
          performcare_data: {
            plan_type: formData.pcPlanType,
            plan_subtype: formData.pcPlanSubtype,
            youth_vision: formData.pcYouthVision,
            family_vision: formData.pcFamilyVision,
            strengths: formData.pcStrengths.filter(s => s.description.trim()),
            needs: formData.pcNeeds.filter(n => n.description.trim()),
            strategies: formData.pcStrategies.filter(s => s.description.trim()),
            diagnosis: formData.pcDiagnosis.code ? formData.pcDiagnosis : null,
            start_date: formData.serviceDate
          },
          auto_saved: true,
          status: 'ai_draft'
        };
        break;
      case 'parent_email':
        endpoint = '/api/ai/save-parent-email-draft';
        payload = {
          client_id: selectedClientId,
          client_name: clientName,
          content: content,
          parent_name: selectedClient?.guardian_name || '',
          subject: formData.subject || `Session Update for ${selectedClient?.first_name || 'Client'}`,
          auto_saved: true
        };
        break;
      default:
        return; // Skip auto-save for unknown types
    }
    
    await axios.post(`${API_URL}${endpoint}`, payload, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  };
  
  // Handle document save (manual save - finalizes the draft)
  const handleSave = async () => {
    setLoading(true);
    setError('');
    
    try {
      const contentToSave = formData.content || generatedContent;
      const clientName = selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Client';
      
      let endpoint = '';
      let payload = {};
      
      switch (docType) {
        case 'progress_note':
          endpoint = '/api/ai/save-soap-note';
          payload = {
            client_id: selectedClientId,
            client_name: clientName,
            content: contentToSave,
            session_date: formData.serviceDate,
            session_type: formData.sessionType
          };
          break;
        case 'treatment_plan':
          endpoint = '/api/ai/save-treatment-plan';
          // Build PerformCare-compliant payload
          payload = {
            client_id: selectedClientId,
            client_name: clientName,
            content: contentToSave,
            plan_type: formData.pcPlanType || 'Treatment Plan Update',
            // PerformCare-specific fields
            performcare_data: {
              plan_type: formData.pcPlanType,
              plan_subtype: formData.pcPlanSubtype,
              youth_vision: formData.pcYouthVision,
              family_vision: formData.pcFamilyVision,
              strengths: formData.pcStrengths.filter(s => s.description.trim()),
              needs: formData.pcNeeds.filter(n => n.description.trim()),
              strategies: formData.pcStrategies.filter(s => s.description.trim()),
              diagnosis: formData.pcDiagnosis.code ? formData.pcDiagnosis : null,
              start_date: formData.serviceDate
            }
          };
          break;
        case 'parent_email':
          endpoint = '/api/ai/save-parent-email-draft';
          payload = {
            client_id: selectedClientId,
            client_name: clientName,
            content: contentToSave,
            parent_name: selectedClient?.guardian_name || '',
            subject: formData.subject || `Session Update for ${selectedClient?.first_name || 'Client'}`
          };
          break;
        default:
          throw new Error('Unknown document type');
      }
      
      const response = await axios.post(`${API_URL}${endpoint}`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSaveSuccess(true);
        toast?.success(`${DOCUMENT_TYPES[docType].name} saved successfully to ${DOCUMENT_TYPES[docType].saveTo}!`);
        onDocumentCreated?.(response.data);
        
        // Auto-close after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error(response.data.error || 'Failed to save document');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message || 'Failed to save document. Please try again.');
      toast?.error('Failed to save document. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">What would you like to create?</h3>
              <p className="text-sm text-gray-500 mt-1">Select the type of clinical document</p>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {Object.values(DOCUMENT_TYPES).map((type) => {
                const Icon = type.icon;
                const isSelected = docType === type.id;
                const isDisabled = type.requiresGuardian && !hasGuardian && selectedClientId;
                
                return (
                  <button
                    key={type.id}
                    onClick={() => !isDisabled && setDocType(type.id)}
                    disabled={isDisabled}
                    className={`
                      flex items-center gap-4 p-4 rounded-lg border-2 text-left transition-all
                      ${isSelected 
                        ? `border-${type.color}-500 bg-${type.color}-50 ring-2 ring-${type.color}-200` 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }
                      ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <div className={`
                      p-3 rounded-full
                      ${isSelected ? `bg-${type.color}-100` : 'bg-gray-100'}
                    `}>
                      <Icon className={`w-6 h-6 ${isSelected ? `text-${type.color}-600` : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{type.name}</div>
                      <div className="text-sm text-gray-500">{type.description}</div>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          Saves to: {type.saveTo}
                        </Badge>
                        {type.aiSupported && (
                          <Badge className="bg-indigo-100 text-indigo-700 text-xs">
                            <Sparkles className="w-3 h-3 mr-1" /> Ready
                          </Badge>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <Check className={`w-6 h-6 text-${type.color}-600`} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Select Client</h3>
              <p className="text-sm text-gray-500 mt-1">Choose the client for this document</p>
            </div>
            
            <div className="space-y-3">
              <Label>Client</Label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a client..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        {client.first_name} {client.last_name}
                        {client.status === 'active' && (
                          <Badge className="ml-2 bg-green-100 text-green-700 text-xs">Active</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedClient && (
                <Card className="mt-4 bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-full">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{selectedClient.first_name} {selectedClient.last_name}</div>
                        <div className="text-sm text-gray-600 mt-1 grid grid-cols-2 gap-x-4 gap-y-1">
                          <span>DOB: {selectedClient.date_of_birth || 'N/A'}</span>
                          <span>Status: {selectedClient.status}</span>
                          {selectedClient.guardian_name && (
                            <span className="col-span-2">Guardian: {selectedClient.guardian_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {docType === 'parent_email' && selectedClient && !hasGuardian && (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    This client has no guardian information. Please update the client profile first.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">How would you like to create it?</h3>
              <p className="text-sm text-gray-500 mt-1">Choose your preferred creation method</p>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {Object.values(CREATION_METHODS).map((method) => {
                const Icon = method.icon;
                const isSelected = creationMethod === method.id;
                const isDisabled = method.id === 'voice' && !DOCUMENT_TYPES[docType]?.voiceSupported;
                const isAiDisabled = method.id === 'ai_assisted' && !DOCUMENT_TYPES[docType]?.aiSupported;
                
                return (
                  <button
                    key={method.id}
                    onClick={() => !isDisabled && !isAiDisabled && setCreationMethod(method.id)}
                    disabled={isDisabled || isAiDisabled}
                    className={`
                      flex items-center gap-4 p-4 rounded-lg border-2 text-left transition-all
                      ${isSelected 
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }
                      ${(isDisabled || isAiDisabled) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <div className={`p-3 rounded-full ${isSelected ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                      <Icon className={`w-6 h-6 ${isSelected ? 'text-indigo-600' : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{method.name}</div>
                      <div className="text-sm text-gray-500">{method.description}</div>
                    </div>
                    {isSelected && <Check className="w-6 h-6 text-indigo-600" />}
                  </button>
                );
              })}
            </div>
          </div>
        );
        
      case 4:
        // Content entry step (different based on method)
        if (creationMethod === 'ai_assisted') {
          return (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Enter Session Information</h3>
                <p className="text-sm text-gray-500 mt-1">Provide notes or bullet points to process</p>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Session Type</Label>
                    <Select 
                      value={formData.sessionType} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, sessionType: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Individual Therapy">Individual Therapy</SelectItem>
                        <SelectItem value="Family Therapy">Family Therapy</SelectItem>
                        <SelectItem value="Group Therapy">Group Therapy</SelectItem>
                        <SelectItem value="Assessment">Assessment</SelectItem>
                        <SelectItem value="Crisis Intervention">Crisis Intervention</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Session Date</Label>
                    <Input
                      type="date"
                      value={formData.serviceDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, serviceDate: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Session Notes / Bullet Points</Label>
                  <Textarea
                    value={formData.rawNotes}
                    onChange={(e) => setFormData(prev => ({ ...prev, rawNotes: e.target.value }))}
                    placeholder="Enter your session notes, bullet points, or rough observations...

Example:
- Client reported improved sleep this week
- Discussed coping strategies for anxiety
- Practiced deep breathing exercise
- Assigned homework: journaling 3x/week
- Next session: review progress on goals"
                    rows={10}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Tip: The more detail you provide, the better the Auto-generated document will be.
                  </p>
                </div>
                
                {error && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <AlertDescription className="text-red-800">{error}</AlertDescription>
                  </Alert>
                )}
                
                <Button
                  onClick={handleAIGenerate}
                  disabled={aiGenerating || !formData.rawNotes.trim()}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 py-6"
                >
                  {aiGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating {DOCUMENT_TYPES[docType]?.name}...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5 mr-2" />
                      Generate {DOCUMENT_TYPES[docType]?.name} with AI
                    </>
                  )}
                </Button>
              </div>
            </div>
          );
        }
        
        // Manual entry
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Create {DOCUMENT_TYPES[docType]?.name}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Enter your content below
              </p>
            </div>
            
            <div className="space-y-4">
              {docType === 'progress_note' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Note Type</Label>
                      <Select 
                        value={formData.noteType} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, noteType: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {NOTE_TYPES.map(nt => (
                            <SelectItem key={nt.value} value={nt.value}>{nt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Date of Service</Label>
                      <Input
                        type="date"
                        value={formData.serviceDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, serviceDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  {availableSessions.length > 0 && (
                    <div>
                      <Label>Related Session (Optional)</Label>
                      <Select 
                        value={formData.sessionId} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, sessionId: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a session..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableSessions.map(session => (
                            <SelectItem key={session.id} value={session.id}>
                              {session.session_date} - {session.session_type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
              
              {docType === 'parent_email' && (
                <div>
                  <Label>Email Subject</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder={`Session Update for ${selectedClient?.first_name || 'Client'}`}
                  />
                </div>
              )}

              {/* PerformCare Treatment Plan Fields */}
              {docType === 'treatment_plan' && (
                <div className="space-y-4">
                  <Card className="border-green-200 bg-green-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-green-800">
                        <Target className="w-4 h-4" />
                        PerformCare Compliance Fields
                        <Badge className="ml-auto bg-green-600">Required</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Plan Type & Subtype */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Plan Type *</Label>
                          <Select 
                            value={formData.pcPlanType} 
                            onValueChange={(v) => setFormData(prev => ({ ...prev, pcPlanType: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select plan type..." />
                            </SelectTrigger>
                            <SelectContent>
                              {PERFORMCARE_PLAN_TYPES.map(pt => (
                                <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label>Plan Subtype *</Label>
                          <Select 
                            value={formData.pcPlanSubtype} 
                            onValueChange={(v) => setFormData(prev => ({ ...prev, pcPlanSubtype: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select subtype..." />
                            </SelectTrigger>
                            <SelectContent>
                              {PERFORMCARE_SUBTYPES.map(ps => (
                                <SelectItem key={ps.value} value={ps.value}>{ps.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Vision Statements */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Youth Vision Statement</Label>
                          <Textarea
                            value={formData.pcYouthVision}
                            onChange={(e) => setFormData(prev => ({ ...prev, pcYouthVision: e.target.value }))}
                            placeholder="What does the youth want to achieve?"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label>Family Vision Statement</Label>
                          <Textarea
                            value={formData.pcFamilyVision}
                            onChange={(e) => setFormData(prev => ({ ...prev, pcFamilyVision: e.target.value }))}
                            placeholder="What does the family want to achieve?"
                            rows={2}
                          />
                        </div>
                      </div>

                      {/* Strengths */}
                      <div>
                        <Label className="flex items-center gap-2">
                          Strengths
                          <Badge variant="outline" className="text-xs">At least 1 required</Badge>
                        </Label>
                        {formData.pcStrengths.map((strength, idx) => (
                          <div key={idx} className="flex gap-2 mt-2">
                            <Input
                              value={strength.description}
                              onChange={(e) => {
                                const updated = [...formData.pcStrengths];
                                updated[idx].description = e.target.value;
                                setFormData(prev => ({ ...prev, pcStrengths: updated }));
                              }}
                              placeholder="Strength description..."
                              className="flex-1"
                            />
                            <Input
                              value={strength.personLinked}
                              onChange={(e) => {
                                const updated = [...formData.pcStrengths];
                                updated[idx].personLinked = e.target.value;
                                setFormData(prev => ({ ...prev, pcStrengths: updated }));
                              }}
                              placeholder="Person linked"
                              className="w-32"
                            />
                            {formData.pcStrengths.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    pcStrengths: prev.pcStrengths.filter((_, i) => i !== idx)
                                  }));
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            pcStrengths: [...prev.pcStrengths, { description: '', personLinked: '' }]
                          }))}
                        >
                          + Add Strength
                        </Button>
                      </div>

                      {/* Needs */}
                      <div>
                        <Label className="flex items-center gap-2">
                          Needs/Goals
                          <Badge variant="outline" className="text-xs">At least 1 required</Badge>
                        </Label>
                        {formData.pcNeeds.map((need, idx) => (
                          <div key={idx} className="flex gap-2 mt-2 items-start">
                            <Input
                              value={need.description}
                              onChange={(e) => {
                                const updated = [...formData.pcNeeds];
                                updated[idx].description = e.target.value;
                                setFormData(prev => ({ ...prev, pcNeeds: updated }));
                              }}
                              placeholder="Need/goal description..."
                              className="flex-1"
                            />
                            <Select
                              value={need.domain}
                              onValueChange={(v) => {
                                const updated = [...formData.pcNeeds];
                                updated[idx].domain = v;
                                setFormData(prev => ({ ...prev, pcNeeds: updated }));
                              }}
                            >
                              <SelectTrigger className="w-36">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {NEED_DOMAINS.map(d => (
                                  <SelectItem key={d} value={d}>{d}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select
                              value={need.progress}
                              onValueChange={(v) => {
                                const updated = [...formData.pcNeeds];
                                updated[idx].progress = v;
                                setFormData(prev => ({ ...prev, pcNeeds: updated }));
                              }}
                            >
                              <SelectTrigger className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Not Met">Not Met</SelectItem>
                                <SelectItem value="In Progress">In Progress</SelectItem>
                                <SelectItem value="Met">Met</SelectItem>
                              </SelectContent>
                            </Select>
                            {formData.pcNeeds.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    pcNeeds: prev.pcNeeds.filter((_, i) => i !== idx)
                                  }));
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            pcNeeds: [...prev.pcNeeds, { description: '', domain: 'Mental Health', progress: 'Not Met' }]
                          }))}
                        >
                          + Add Need
                        </Button>
                      </div>

                      {/* Strategies/Interventions */}
                      <div>
                        <Label className="flex items-center gap-2">
                          Strategies/Interventions
                          <Badge variant="outline" className="text-xs">At least 1 required</Badge>
                        </Label>
                        {formData.pcStrategies.map((strategy, idx) => (
                          <div key={idx} className="flex gap-2 mt-2">
                            <Input
                              value={strategy.description}
                              onChange={(e) => {
                                const updated = [...formData.pcStrategies];
                                updated[idx].description = e.target.value;
                                setFormData(prev => ({ ...prev, pcStrategies: updated }));
                              }}
                              placeholder="Strategy/intervention description..."
                              className="flex-1"
                            />
                            <Input
                              value={strategy.responsiblePerson}
                              onChange={(e) => {
                                const updated = [...formData.pcStrategies];
                                updated[idx].responsiblePerson = e.target.value;
                                setFormData(prev => ({ ...prev, pcStrategies: updated }));
                              }}
                              placeholder="Responsible person"
                              className="w-40"
                            />
                            {formData.pcStrategies.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    pcStrategies: prev.pcStrategies.filter((_, i) => i !== idx)
                                  }));
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            pcStrategies: [...prev.pcStrategies, { description: '', responsiblePerson: '' }]
                          }))}
                        >
                          + Add Strategy
                        </Button>
                      </div>

                      {/* Diagnosis */}
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>ICD-10 Code</Label>
                          <Input
                            value={formData.pcDiagnosis.code}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              pcDiagnosis: { ...prev.pcDiagnosis, code: e.target.value }
                            }))}
                            placeholder="e.g., F41.1"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Diagnosis Description</Label>
                          <Input
                            value={formData.pcDiagnosis.description}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              pcDiagnosis: { ...prev.pcDiagnosis, description: e.target.value }
                            }))}
                            placeholder="e.g., Generalized Anxiety Disorder"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Additional Notes/Content */}
                  <div>
                    <Label>Additional Notes (Optional)</Label>
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                      placeholder="Any additional clinical notes or observations..."
                      rows={4}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Content textarea for non-treatment-plan documents */}
              {docType !== 'treatment_plan' && (
              <div>
                <Label>Content</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder={
                    docType === 'progress_note' 
                      ? "SUBJECTIVE:\n\nOBJECTIVE:\n\nASSESSMENT:\n\nPLAN:"
                      : "Enter email content..."
                  }
                  rows={12}
                  className="font-mono text-sm"
                />
              </div>
              )}
              
              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <AlertDescription className="text-red-800">{error}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        );
        
      case 5:
        // Review & Save step (for Auto-generated content)
        return (
          <div className="space-y-4">
            {saveSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-green-800">Document Saved!</h3>
                <p className="text-gray-600 mt-2">
                  Your {DOCUMENT_TYPES[docType]?.name} has been saved to {DOCUMENT_TYPES[docType]?.saveTo}
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Review & Save</h3>
                  <p className="text-sm text-gray-500 mt-1">Review and edit before saving</p>
                </div>
                
                <div className="space-y-4">
                  <Card className="bg-gray-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        {docType && DOCUMENT_TYPES[docType]?.icon && (() => {
                          const IconComponent = DOCUMENT_TYPES[docType].icon;
                          return <IconComponent className="w-4 h-4" />;
                        })()}
                        {DOCUMENT_TYPES[docType]?.name}
                        <Badge className="ml-auto bg-indigo-100 text-indigo-700">
                          <Sparkles className="w-3 h-3 mr-1" /> Auto-Generated
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="text-xs text-gray-500 mb-2">
                        Name: <strong>{selectedClient?.first_name} {selectedClient?.last_name}</strong>
                        {' | '}Date: <strong>{formData.serviceDate}</strong>
                        {' | '}Will save to: <strong>{DOCUMENT_TYPES[docType]?.saveTo}</strong>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <div>
                    <Label>Content (editable)</Label>
                    <Textarea
                      value={formData.content || generatedContent}
                      onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                      rows={14}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 You can edit the content above before saving.
                    </p>
                  </div>
                  
                  {error && (
                    <Alert className="border-red-200 bg-red-50">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      <AlertDescription className="text-red-800">{error}</AlertDescription>
                    </Alert>
                  )}
                </div>
              </>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };
  
  // Get step title for progress indicator
  const getStepTitle = (stepNum) => {
    switch (stepNum) {
      case 1: return 'Document Type';
      case 2: return 'Client';
      case 3: return 'Method';
      case 4: return creationMethod === 'ai_assisted' ? 'AI Input' : 'Content';
      case 5: return 'Review';
      default: return '';
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Create Document
            <Badge className="ml-2 bg-indigo-100 text-indigo-700">Unified Workflow</Badge>
          </DialogTitle>
          <DialogDescription>
            Create clinical documents with guided assistance and PerformCare compliance
          </DialogDescription>
        </DialogHeader>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-between px-2 py-3 border-b flex-shrink-0">
          {Array.from({ length: getTotalSteps() }, (_, i) => i + 1).map((stepNum) => (
            <div key={stepNum} className="flex items-center">
              <div className={`
                flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                ${step > stepNum ? 'bg-green-100 text-green-700' : 
                  step === stepNum ? 'bg-indigo-600 text-white' : 
                  'bg-gray-100 text-gray-400'}
              `}>
                {step > stepNum ? <Check className="w-4 h-4" /> : stepNum}
              </div>
              <span className={`ml-2 text-xs hidden sm:inline ${step === stepNum ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
                {getStepTitle(stepNum)}
              </span>
              {stepNum < getTotalSteps() && (
                <ChevronRight className="w-4 h-4 mx-2 text-gray-300" />
              )}
            </div>
          ))}
        </div>
        
        {/* Step Content */}
        <div className="flex-1 overflow-y-auto py-4 px-1">
          {renderStepContent()}
        </div>
        
        {/* Footer Navigation */}
        {!saveSuccess && (
          <div className="flex justify-between items-center pt-4 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => step > 1 ? setStep(step - 1) : onClose()}
              disabled={loading || aiGenerating}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {step === 1 ? 'Cancel' : 'Back'}
            </Button>
            
            <div className="flex gap-2">
              {step === getTotalSteps() || (step === 4 && creationMethod !== 'ai_assisted') ? (
                <Button
                  onClick={handleSave}
                  disabled={loading || !canProceed()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save to {DOCUMENT_TYPES[docType]?.saveTo}
                    </>
                  )}
                </Button>
              ) : (
                step < 4 && (
                  <Button
                    onClick={() => setStep(step + 1)}
                    disabled={!canProceed() || aiGenerating}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedDocumentCreator;
